import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { Minus, Plus, CheckCircle2, ArrowLeft } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { useEvent } from '../context/EventContext';
import { store } from '../lib/localStore';
import { api } from '../lib/api';

export default function Order() {
  const { t } = useLang();
  const { config } = useEvent();
  const products = config?.products || [];
  const eventId = config?.event?.id;

  const [step, setStep] = useState('select'); // select | details | done
  const [qty, setQty] = useState({});
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', comment: '' });
  const [submitting, setSubmitting] = useState(false);
  const idempotencyKey = useRef(null);

  useEffect(() => {
    if (!eventId) return;
    // prefill quantities from tasting selections + saved contact
    const tasting = store.getTasting(eventId);
    const initial = {};
    products.forEach((p) => { if (tasting[p.id]?.quantity) initial[p.id] = tasting[p.id].quantity; });
    setQty(initial);
    const c = store.getContact();
    setForm((f) => ({ ...f, first_name: c.first_name || '', last_name: c.last_name || '', email: c.email || '', phone: c.phone || '' }));
    // prefill from the shared participant record (e.g. created during the quiz)
    const token = store.getToken(eventId);
    api.getParticipant(eventId, token).then((p) => {
      if (p.found) setForm((f) => ({
        ...f,
        first_name: f.first_name || p.first_name, last_name: f.last_name || p.last_name,
        email: f.email || p.email, phone: f.phone || p.phone,
      }));
    }).catch(() => {});
  }, [eventId, products.length]);

  const items = useMemo(
    () => products.filter((p) => (qty[p.id] || 0) > 0).map((p) => ({ product_id: p.id, product_name: p.name, quantity: qty[p.id] })),
    [qty, products]
  );
  const totalBottles = items.reduce((s, i) => s + i.quantity, 0);

  const setBottle = (id, v) => setQty((q) => ({ ...q, [id]: Math.max(0, v) }));

  const goReview = () => {
    if (items.length === 0) { toast.error(t('orderEmptyError')); return; }
    idempotencyKey.current = (window.crypto?.randomUUID?.() || `ord-${Date.now()}-${Math.random()}`);
    setStep('details');
  };

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error(t('requiredFields')); return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      const token = store.getToken(eventId);
      const res = await api.createOrder({
        event_id: eventId, token, idempotency_key: idempotencyKey.current,
        first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone,
        comment: form.comment, items,
      });
      if (res.ok) {
        store.setContact({ first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone });
        setStep('done');
      } else { toast.error(t('errorGeneric')); }
    } catch (e) {
      toast.error(e?.response?.status ? t('errorGeneric') : t('connectionIssue'));
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'done') {
    return (
      <div className="text-center py-10" data-testid="order-confirmation">
        <CheckCircle2 size={56} className="text-[#2D6A4F] mx-auto" />
        <h1 className="font-serif font-bold text-2xl text-[#E5C158] mt-4">{t('orderConfirmedTitle')}</h1>
        <p className="font-sans text-sm text-slate-300 mt-3 leading-relaxed px-2">{t('orderConfirmed')}</p>
        <button
          data-testid="order-done-button"
          onClick={() => { setStep('select'); setQty({}); }}
          className="mt-8 w-full border border-[#D4AF37] text-[#E5C158] font-semibold py-3.5 rounded-xl"
        >
          {t('done')}
        </button>
      </div>
    );
  }

  if (step === 'details') {
    return (
      <div data-testid="order-details">
        <button onClick={() => setStep('select')} className="flex items-center gap-1 text-sm text-[#E5C158] mb-4"><ArrowLeft size={16} /> {t('backToSelection')}</button>
        <h1 className="font-serif font-bold text-2xl text-[#FDFBF7]">{t('reviewRequest')}</h1>

        <div className="mt-4 bg-[#111A38] border border-[#D4AF37]/20 rounded-xl p-4 space-y-2">
          {items.map((i) => (
            <div key={i.product_id} className="flex justify-between text-sm">
              <span className="text-slate-200">{i.product_name}</span>
              <span className="font-mono text-[#E5C158]">×{i.quantity}</span>
            </div>
          ))}
          <div className="flex justify-between text-sm border-t border-[#D4AF37]/15 pt-2 mt-2">
            <span className="text-slate-400">{t('bottles')}</span>
            <span className="font-mono text-[#E5C158]">{totalBottles}</span>
          </div>
        </div>

        <h2 className="font-serif text-lg text-[#E5C158] mt-6 mb-2">{t('yourDetails')}</h2>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="order-first-name-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder={t('firstName')} className="w-full bg-[#070C1E] border border-[#D4AF37]/25 rounded-lg p-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#D4AF37] outline-none" />
            <input data-testid="order-last-name-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder={t('lastName')} className="w-full bg-[#070C1E] border border-[#D4AF37]/25 rounded-lg p-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#D4AF37] outline-none" />
          </div>
          <input data-testid="order-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t('email')} className="w-full bg-[#070C1E] border border-[#D4AF37]/25 rounded-lg p-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#D4AF37] outline-none" />
          <input data-testid="order-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t('phone')} className="w-full bg-[#070C1E] border border-[#D4AF37]/25 rounded-lg p-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#D4AF37] outline-none" />
          <textarea data-testid="order-comment-input" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder={t('optionalComment')} rows={2} className="w-full bg-[#070C1E] border border-[#D4AF37]/25 rounded-lg p-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#D4AF37] outline-none resize-none" />
        </div>

        <p className="text-[11px] text-slate-500 mt-3">{t('noPaymentNote')}</p>

        <button
          data-testid="order-submit-button"
          onClick={submit}
          disabled={submitting}
          className="mt-4 w-full bg-gradient-to-r from-[#D4AF37] to-[#E5C158] text-[#070C1E] font-bold py-4 rounded-xl active:scale-[0.98] transition-all disabled:opacity-60"
        >
          {submitting ? t('loading') : t('sendRequest')}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="order-screen">
      <h1 className="font-serif font-bold text-3xl text-[#FDFBF7]">{t('order_title')}</h1>
      <p className="font-sans text-sm text-slate-400 mb-4">{t('order_intro')}</p>

      <div className="space-y-3">
        {products.map((p) => (
          <div key={p.id} data-testid={`order-item-${p.order}`} className="flex items-center justify-between bg-[#111A38] border border-[#D4AF37]/20 rounded-xl p-3.5">
            <div className="pr-2">
              <h3 className="font-serif text-base text-[#FDFBF7] leading-tight">{p.name}</h3>
              <p className="text-[11px] text-slate-500">{p.kind}</p>
            </div>
            <div className="flex items-center bg-[#070C1E] border border-[#D4AF37]/30 rounded-lg shrink-0">
              <button data-testid={`order-qty-dec-${p.order}`} onClick={() => setBottle(p.id, (qty[p.id] || 0) - 1)} className="p-2.5 text-[#E5C158]"><Minus size={15} /></button>
              <span className="w-7 text-center font-mono text-slate-100">{qty[p.id] || 0}</span>
              <button data-testid={`order-qty-inc-${p.order}`} onClick={() => setBottle(p.id, (qty[p.id] || 0) + 1)} className="p-2.5 text-[#E5C158]"><Plus size={15} /></button>
            </div>
          </div>
        ))}
      </div>

      <button
        data-testid="order-review-button"
        onClick={goReview}
        className="mt-6 w-full bg-gradient-to-r from-[#D4AF37] to-[#E5C158] text-[#070C1E] font-bold py-4 rounded-xl active:scale-[0.98] transition-all"
      >
        {t('reviewRequest')} {totalBottles > 0 && `· ${totalBottles} ${t('bottles')}`}
      </button>
    </div>
  );
}
